import { CommonModule, formatDate } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { OperatorOutageReplyService } from '../../../api/api-services/operator-outage-reply.service';
import { OperatorOutageReportsService } from '../../../api/api-services/operator-outage-reports.service';
import { OperatorOutageResolvedNotificationService } from '../../../api/api-services/operator-outage-resolved-notification.service';
import { IOperatorOutageReportsVm } from '../../../api/operator-outage-reports-vm.model';
import { IOutageFeedbackVm } from '../../../api/outage-feedback-vm.model';
import { IOutageNotificationStatusVm } from '../../../api/outage-notification-status-vm.model';
import { IOutageRecipientVm } from '../../../api/outage-recipient-vm.model';
import { ClientDeviceComponent } from '../../shared/components/client-device/client-device.component';
import {
  buildReplyBody,
  IReplyThreadItem,
  ReplyDialogComponent,
} from '../../shared/components/reply-dialog/reply-dialog.component';
import { UserAccountLinkComponent } from '../../shared/components/user-account-link/user-account-link.component';

interface ISummaryCard {
  label: string;
  icon: string;
  value: string;
  detail: string;
  highlight?: boolean;
}

interface IReplyTarget {
  email: string;
  userGuid?: string;
  thread: IReplyThreadItem[];
}

const REASON_LABELS: Record<string, string> = {
  login: 'Logowanie',
  timeouts: 'Brak odpowiedzi',
  'server-unavailable': 'Serwer niedostępny',
};

const DEFAULT_SUBJECT = 'ZdamyPrawko – aplikacja działa już normalnie';
const DEFAULT_BODY = [
  'Dzień dobry,',
  '',
  'dziękujemy za zgłoszenie i cierpliwość. Problem z działaniem naszego serwera został rozwiązany — aplikacja ZdamyPrawko działa już normalnie.',
  '',
  'Przepraszamy za utrudnienia.',
  '',
  'Zespół ZdamyPrawko',
].join('\n');

const REPLY_SUBJECT = 'ZdamyPrawko – odpowiedź na zgłoszenie';

/**
 * Messages users left on the app's maintenance screen, database outages logged by the API,
 * the "outage resolved" mailing to everyone who reported a problem since their last such email
 * and replies to single users (a reply counts as that email).
 */
@Component({
  selector: 'app-outage-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    InputTextareaModule,
    ProgressBarModule,
    ProgressSpinnerModule,
    TableModule,
    TagModule,
    TooltipModule,
    ClientDeviceComponent,
    UserAccountLinkComponent,
    ReplyDialogComponent,
  ],
  templateUrl: './outage-reports.component.html',
  styleUrl: './outage-reports.component.scss',
})
export class OutageReportsComponent implements OnInit, OnDestroy {
  private _reportsService = inject(OperatorOutageReportsService);
  private _notificationService = inject(OperatorOutageResolvedNotificationService);
  private _replyService = inject(OperatorOutageReplyService);

  readonly periodOptions = [
    { label: 'Ostatnie 7 dni', value: 7 },
    { label: 'Ostatnie 30 dni', value: 30 },
    { label: 'Ostatnie 90 dni', value: 90 },
  ];
  days = 30;

  loading = false;
  reports: IOperatorOutageReportsVm | null = null;
  cards: ISummaryCard[] = [];
  errorMessage: string | null = null;
  successMessage: string | null = null;

  subject = DEFAULT_SUBJECT;
  body = DEFAULT_BODY;
  status: IOutageNotificationStatusVm | null = null;
  starting = false;
  cancelling = false;

  replyVisible = false;
  replyTarget: IReplyTarget | null = null;
  replySubject = REPLY_SUBJECT;
  replyBody = '';
  replying = false;
  replyError: string | null = null;

  private _recipientsByEmail = new Map<string, IOutageRecipientVm>();

  private _statusTimer: any = null;
  // A slow response must never overwrite the state of a newer one (same guard as the Serwer tab).
  private _reqSeq = 0;

  ngOnInit(): void {
    this.load();
    this.loadStatus();
  }

  ngOnDestroy(): void {
    this._stopStatusPolling();
  }

  get canSend(): boolean {
    return (
      !!this.reports?.awaitingNotificationCount &&
      !this.status?.isRunning &&
      !this.starting &&
      !!this.subject.trim() &&
      !!this.body.trim()
    );
  }

  get progressPercent(): number {
    if (!this.status?.total) return 0;
    return Math.round(((this.status.sent + this.status.failed) / this.status.total) * 100);
  }

  load(): void {
    const seq = ++this._reqSeq;
    this.loading = true;
    this._reportsService.get(this.days).subscribe({
      next: (reports) => {
        if (seq !== this._reqSeq) return;
        this.reports = reports;
        this.cards = this._buildCards(reports);
        this._recipientsByEmail = new Map(reports.recipients.map((x) => [x.email, x]));
        this.loading = false;
      },
      error: (e) => {
        if (seq !== this._reqSeq) return;
        this.loading = false;
        this.errorMessage = e?.error?.message ?? 'Nie udało się pobrać zgłoszeń.';
      },
    });
  }

  refresh(): void {
    this.errorMessage = null;
    this.successMessage = null;
    this.load();
    this.loadStatus();
  }

  loadStatus(): void {
    this._notificationService.get().subscribe({
      next: (status) => this._applyStatus(status),
      error: () => this._stopStatusPolling(),
    });
  }

  send(): void {
    const count = this.reports?.awaitingNotificationCount ?? 0;
    const confirmed = confirm(
      `Wysłać wiadomość „${this.subject.trim()}” do ${count} ${count === 1 ? 'osoby' : 'osób'}? ` +
        'Wysyłka idzie w tle, po jednej wiadomości co kilka sekund.',
    );
    if (!confirmed) return;

    this.starting = true;
    this.errorMessage = null;
    this._notificationService
      .post({ subject: this.subject.trim(), body: this.body.trim(), days: this.days })
      .subscribe({
        next: (status) => {
          this.starting = false;
          this._applyStatus(status);
        },
        error: (e) => {
          this.starting = false;
          this.errorMessage = e?.error?.message ?? 'Nie udało się rozpocząć wysyłki.';
        },
      });
  }

  cancel(): void {
    if (!confirm('Przerwać wysyłkę? Osoby, do których wiadomość jeszcze nie wyszła, pozostaną na liście oczekujących.')) {
      return;
    }
    this.cancelling = true;
    this._notificationService.delete().subscribe({
      next: (status) => {
        this.cancelling = false;
        this._applyStatus(status);
      },
      error: (e) => {
        this.cancelling = false;
        this.errorMessage = e?.error?.message ?? 'Nie udało się przerwać wysyłki.';
      },
    });
  }

  resetMessage(): void {
    this.subject = DEFAULT_SUBJECT;
    this.body = DEFAULT_BODY;
  }

  /** `report` is quoted in the reply; without it, the user's latest one is. */
  openReply(email: string, report?: IOutageFeedbackVm): void {
    // Both lists come newest first.
    const reports = this.reports?.feedback.filter((x) => x.email === email) ?? [];
    // An API older than the reply feature sends no replies.
    const replies = this.reports?.replies?.filter((x) => x.email === email) ?? [];
    const thread: IReplyThreadItem[] = [
      ...reports.map((x) => ({
        at: x.receivedAt,
        outgoing: false,
        label: `zgłoszenie · ${this.reasonLabel(x.reason)}`,
        text: x.message,
        current: x === (report ?? reports[0]),
      })),
      ...replies.map((x) => ({
        at: x.sentAt,
        outgoing: true,
        label: x.sentBy ? `odpowiedź · ${x.sentBy}` : 'odpowiedź',
        subject: x.subject,
        text: x.body ?? '',
      })),
    ].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));

    this.replyTarget = { email, userGuid: reports.find((x) => x.userGuid)?.userGuid, thread };
    this.replySubject = REPLY_SUBJECT;
    const quoted = report ?? reports[0];
    this.replyBody = buildReplyBody(
      'dziękujemy za zgłoszenie.',
      quoted && {
        header: `Twoja wiadomość z ${formatDate(quoted.receivedAt, 'yyyy-MM-dd HH:mm', 'en-US')}:`,
        text: quoted.message,
      },
    );
    this.replyError = null;
    this.successMessage = null;
    this.replyVisible = true;
  }

  sendReply(): void {
    if (!this.replyTarget || this.replying) return;

    const email = this.replyTarget.email;
    this.replying = true;
    this.replyError = null;
    this._replyService
      .post({ email, subject: this.replySubject.trim(), body: this.replyBody.trim(), days: this.days })
      .subscribe({
        next: () => {
          this.replying = false;
          this.replyVisible = false;
          this.successMessage = `Wysłano odpowiedź do ${email}.`;
          // The recipient's status and the reply history change.
          this.load();
        },
        error: (e) => {
          this.replying = false;
          this.replyError = e?.error?.message ?? 'Nie udało się wysłać odpowiedzi.';
        },
      });
  }

  /** When the reporter got a reply or the "outage resolved" e-mail after this report, if they did. */
  answeredAt(report: IOutageFeedbackVm): string | null {
    const notifiedAt = report.email ? this._recipientsByEmail.get(report.email)?.lastNotifiedAt : null;
    return notifiedAt && Date.parse(notifiedAt) >= Date.parse(report.receivedAt) ? notifiedAt : null;
  }

  reasonLabel(reason?: string): string {
    return reason ? REASON_LABELS[reason] ?? reason : '—';
  }

  outageDuration(startedAt: string, endedAt?: string | null): string {
    const minutes = Math.max(0, Math.round((Date.parse(endedAt ?? new Date().toISOString()) - Date.parse(startedAt)) / 60000));
    if (minutes < 60) return `${minutes} min`;
    return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
  }

  private _applyStatus(status: IOutageNotificationStatusVm): void {
    const wasRunning = this.status?.isRunning;
    this.status = status;
    if (status.isRunning) {
      this._startStatusPolling();
    } else {
      this._stopStatusPolling();
      // The recipients' "notified" state changes as the mailing ends.
      if (wasRunning) this.load();
    }
  }

  private _startStatusPolling(): void {
    if (!this._statusTimer) {
      this._statusTimer = setInterval(() => this.loadStatus(), 3000);
    }
  }

  private _stopStatusPolling(): void {
    if (this._statusTimer) {
      clearInterval(this._statusTimer);
      this._statusTimer = null;
    }
  }

  private _buildCards(reports: IOperatorOutageReportsVm): ISummaryCard[] {
    const openOutages = reports.databaseOutages.filter((x) => !x.recoveredAt).length;
    return [
      {
        label: 'Zgłoszenia',
        icon: 'pi pi-comments',
        value: `${reports.feedback.length}`,
        detail: `z ostatnich ${reports.days} dni`,
      },
      {
        label: 'Adresy e-mail',
        icon: 'pi pi-envelope',
        value: `${reports.recipients.length}`,
        detail: `${reports.feedback.filter((x) => !x.email).length} zgłoszeń bez adresu`,
      },
      {
        label: 'Czeka na powiadomienie',
        icon: 'pi pi-send',
        value: `${reports.awaitingNotificationCount}`,
        detail: 'zgłosili problem po ostatniej wiadomości',
        highlight: reports.awaitingNotificationCount > 0,
      },
      {
        label: 'Awarie bazy danych',
        icon: 'pi pi-database',
        value: `${reports.databaseOutages.length}`,
        detail: openOutages ? `${openOutages} bez zapisu o przywróceniu` : 'wszystkie zakończone',
        highlight: reports.databaseDown,
      },
    ];
  }
}
